<template>
  <section class="bg-base-100 rounded-xl p-4 sm:p-6 shadow space-y-4 sm:space-y-6">
    <header class="space-y-2">
      <h2 class="text-xl sm:text-2xl font-bold">{{ t("accountCreation.title") }}</h2>
      <p class="text-sm sm:text-base text-base-content/80">{{ t("accountCreation.subtitle") }}</p>
    </header>

    <form @submit.prevent="submit" class="space-y-4">
      <div>
        <label for="email" class="block font-medium text-sm sm:text-base mb-1">{{ t("form.email") }}</label>
        <input
          v-model="email"
          id="email"
          type="email"
          autocomplete="email"
          class="input input-bordered w-full text-base py-3"
          :class="{ 'input-error': errors.email }" />
        <p v-if="errors.email" class="text-error text-sm mt-1">{{ errors.email }}</p>
      </div>

      <button class="btn btn-primary w-full py-3 text-base touch-manipulation" :disabled="loading">
        {{ loading ? t("form.loading") : t("form.continue") }}
      </button>

      <p v-if="error" class="text-error text-sm text-center mt-2">{{ error }}</p>
    </form>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useCheckoutStore } from "../stores/checkoutStore";
import WhmcsApiService from "@/services/WhmcsApiService";

const { t } = useI18n();
const checkoutStore = useCheckoutStore();

// Initialize form from checkoutStore if present
const email = ref(checkoutStore.form.email || "");
// const password = ref(checkoutStore.form.password || "");

const error = ref("");
const loading = ref(false);
const accountExists = ref(false);
const errors = ref<Record<string, string>>({});

function validate(): boolean {
  errors.value = {};

  if (!email.value.trim()) {
    errors.value.email = t("form.validation.required");
  } else if (!/^\S+@\S+\.\S+$/.test(email.value)) {
    errors.value.email = t("form.validation.invalidEmail");
  }

  return Object.keys(errors.value).length === 0;
}

function resetForm() {
  email.value = "";
  loading.value = false;
}

async function submit() {
  if (!validate()) return;

  loading.value = true;
  error.value = "";
  accountExists.value = false;

  try {
    const response = await WhmcsApiService.lookupClient({ email: email.value });

    if (response.exists) {
      resetForm();
      accountExists.value = true;
      error.value = t("accountCreation.alreadyRegistered");
    } else {
      proceedToNextStep();
    }
  } catch (e: any) {
    if (e.response?.status === 404) {
      proceedToNextStep();
    } else {
      error.value = t("form.genericError");
    }
  } finally {
    loading.value = false;
  }
}

function proceedToNextStep() {
  checkoutStore.form.email = email.value;
  // checkoutStore.form.password = password.value;
  checkoutStore.currentStep = 2;
}
</script>
